/**
 * `?focus=<key>` — point at one rail card and say "that one".
 *
 * `docs/quiet-records.md` §4 and §7: two of a property's first steps are not
 * new records at all. **Add access notes** and **Finish the details** are
 * fields on cards that are already on the page, so the step cannot open a
 * form — the honest thing is to take somebody to the card and make it
 * obvious which one.
 *
 * So a step navigates to `…?focus=site` or `…?focus=about`, and the card
 * named by that key scrolls itself into view, rings for 1.2 s and does
 * whatever *focus* means for that card: About opens its edit form, Site &
 * access puts the caret in the Access notes box.
 *
 * **The parameter is removed as soon as it is read** (`replace`, so it leaves
 * no history entry). A `?focus=` left in the URL fires again on every reload
 * and lands in anything copied out of the address bar — a link to a property
 * that opens an edit form is not a link to a property.
 *
 * **`CardFocus` renders nothing, and that is the design.** The hook reads the
 * URL, so a component that calls it needs a `<Router>` above it; `RecordCard`
 * is drawn by two dozen screens and by a dozen tests that render it bare, and
 * making every one of them provide a router to get a feature two cards use
 * would be the wrong way round. A card with no `focusKey` therefore mounts
 * none of this.
 */
import { useEffect, useRef, type MutableRefObject } from 'react'
import { useSearchParams } from 'react-router-dom'

export function CardFocus({ focusKey, box, onRing, onFocus }: {
  focusKey: string
  /** The card's own element, so it can scroll itself into view. */
  box: MutableRefObject<HTMLElement | null>
  /** True for 1.2 s after it was pointed at. */
  onRing: (ringing: boolean) => void
  onFocus?: () => void
}) {
  const [params, setParams] = useSearchParams()
  const wanted = params.get('focus') === focusKey

  /*
   * The two callbacks through refs, and the effect depends on `wanted` alone.
   *
   * A card's `onFocus` is written inline at the call site, so it is a new
   * function on every render; in the dependency list it would re-run this —
   * and re-opening the edit form, re-scrolling and re-ringing on every
   * keystroke in that form is the loop that would cause.
   */
  const latest = useRef({ onRing, onFocus })
  latest.current = { onRing, onFocus }

  useEffect(() => {
    if (!wanted) return
    /* `nearest`, so a card already on screen does not jump; the sticky
       record header is cleared by the card's own `scroll-margin-top`. */
    box.current?.scrollIntoView({ block: 'nearest' })
    latest.current.onRing(true)
    latest.current.onFocus?.()

    const next = new URLSearchParams(params)
    next.delete('focus')
    /* `item` names a row inside the card (Next up's appointment, §11.4) and goes with it. */
    next.delete('item')
    setParams(next, { replace: true })

    const timer = window.setTimeout(() => latest.current.onRing(false), 1200)
    return () => window.clearTimeout(timer)
    /* `wanted` alone, deliberately — see the note above `latest`. */
  }, [wanted])

  return null
}
